import { AxiosResponse } from 'axios'
import { TreeNode } from 'primevue/treenode'
import { IFolderApiResponse } from 'src/system/content/api/IFolderApiResponse'
import { IResourceApiResponse } from 'src/system/content/api/IResourceApiResponse'
import { IStructureApiResponse } from 'src/system/content/api/IStructureApiResponse'
import { FolderDocType, FolderDocument } from 'src/system/content/schemas/folder'
import { ResourceDocType, ResourceDocument } from 'src/system/content/schemas/resource'
import ILocalContentService from 'src/system/content/services/ILocalContentService'
import IRemoteContentService from 'src/system/content/services/IRemoteContentService'
import IResourceService from 'src/system/content/services/IResourceService'

export class ResourceService implements IResourceService {
  constructor(private localContentService: ILocalContentService, private remoteContentService: IRemoteContentService) {
    //
  }

  async fetchAllResources(): Promise<void> {
    const response: AxiosResponse<IStructureApiResponse> = await this.remoteContentService.request(
      'GET',
      '/api/structure'
    )

    let allFolders: FolderDocType[] = []
    const allResources: ResourceDocType[] = []

    const getAllFoldersRecursively = (folder: IFolderApiResponse): IFolderApiResponse[] => {
      let folders = [folder]

      for (const subFolder of folder.children) {
        folders = folders.concat(getAllFoldersRecursively(subFolder))
      }

      return folders
    }

    for (const folder of response.data) {
      const _ = getAllFoldersRecursively(folder).map((folder) => {
        for (const resource of folder.resources) {
          const compositeKey = `${resource.type}-${resource.id}`

          const existingResource = allResources
            .filter((resource) => `${resource.type}-${resource.resourceId}` === compositeKey)
            .at(0)

          if (existingResource) {
            continue
          }

          allResources.push({
            resourceId: resource.id.toString(),
            type: resource.type,
            folders: resource.folders.map((folder) => folder.id.toString()) || [],
            name: resource.name,
            description: resource.description,
            thumbnail: resource.thumbnail,
            image: resource.image,
            document: resource.document,
            url: resource.url,
            video: resource.video,
            fileSize: resource.file_size,
            createdAt: resource.created_at,
            updatedAt: resource.updated_at,
          })
        }

        if (allFolders.some((_) => _.id === folder.id.toString())) {
          return null
        }

        return {
          id: folder.id.toString(),
          parents: folder.parents.map((parent) => parent.toString()) || [],
          name: folder.name,
          description: folder.description,
          thumbnail: folder.thumbnail,
          resources: folder.resources.map((resource) => resource.id.toString()) || [],
          createdAt: folder.created_at,
          updatedAt: folder.updated_at,
        }
      })

      allFolders = allFolders.concat(_.filter((folder) => folder !== null))
    }

    const db = this.localContentService.getDatabase()

    const storedFolderIds = (await db.folders.find().exec()).map((folder) => folder.id)
    const responseFolderIds = allFolders.map((folder) => folder.id)

    const folderIdsToDelete = storedFolderIds.filter((id) => !responseFolderIds.includes(id))

    const storedResourceIds = (await db.resources.find().exec()).map((resource) => resource.id)
    const responseResourceIds = allResources.map((resource) => `${resource.type}-${resource.resourceId}`)

    const resourceIdsToDelete = storedResourceIds.filter((id) => !responseResourceIds.includes(id))

    await Promise.all([
      db.folders.bulkRemove(folderIdsToDelete),
      db.resources.bulkRemove(resourceIdsToDelete),
      db.folders.bulkUpsert(allFolders),
      db.resources.bulkUpsert(allResources),
    ])
  }

  async search(searchTerm: string): Promise<string[][]> {
    const response: AxiosResponse<(IFolderApiResponse | IResourceApiResponse)[]> =
      await this.remoteContentService.request('GET', `/api/search?q=${searchTerm}`)

    const folders: FolderDocType[] = Object.values(response.data)
      .filter((result) => result.type === 'folder')
      .map((folder) => ({
        id: folder.id.toString(),
        parents: folder.parents?.map((parent) => parent.toString()) || [],
        name: folder.name,
        description: folder.description,
        thumbnail: folder.thumbnail,
        resources: folder.resources?.map((resource) => resource.id.toString()) || [],
        createdAt: folder.created_at,
        updatedAt: folder.updated_at,
      }))

    const resources: ResourceDocType[] = Object.values(response.data)
      .filter((result) => result.type !== 'folder')
      .map((resource: IResourceApiResponse) => ({
        resourceId: resource.id.toString(),
        type: resource.type,
        folders: resource.folders.map((folder) => folder.id.toString()) || [],
        name: resource.name,
        description: resource.description,
        thumbnail: resource.thumbnail,
        image: resource.image,
        document: resource.document,
        url: resource.url,
        video: resource.video,
        fileSize: resource.file_size,
        createdAt: resource.created_at,
        updatedAt: resource.updated_at,
      }))

    const db = this.localContentService.getDatabase()

    await Promise.all([db.folders.bulkUpsert(folders), db.resources.bulkUpsert(resources)])

    return [folders.map((folder) => folder.id), resources.map((resource) => `${resource.type}-${resource.resourceId}`)]
  }

  async getResourceTree(includeResources = true): Promise<TreeNode[]> {
    const db = this.localContentService.getDatabase()

    const [allFolders, allResources] = await Promise.all([db.folders.find().exec(), db.resources.find().exec()])

    const folderMap = new Map<string, FolderDocument>()
    allFolders.forEach((folder) => folderMap.set(folder.id, folder))

    const rootFolders = allFolders.filter((folder) => folder.parents.length === 0)

    const buildFolderNode = (folderId: string): TreeNode | null => {
      const folder = folderMap.get(folderId)

      if (!folder) {
        return null
      }

      const subFolders = allFolders
        .filter((folder) => folder.parents.includes(folderId))
        .map((folder) => buildFolderNode(folder.id))
        .filter((node): node is TreeNode => node !== null)

      const resources = includeResources
        ? allResources
            .filter((resource) => resource.folders.includes(folderId))
            .map(
              (resource): TreeNode => ({
                key: `${resource.id}`,
                label: resource.name,
                data: resource,
                selectable: true,
              })
            )
        : []

      return {
        key: `folder-${folder.id}`,
        label: folder.name,
        data: folder,
        children: [...subFolders, ...resources],
        selectable: true,
      }
    }

    return rootFolders.map((folder) => buildFolderNode(folder.id)).filter((node): node is TreeNode => node !== null)
  }

  async saveResource(resource: IResourceApiResponse): Promise<ResourceDocument> {
    const documentData: ResourceDocType = {
      resourceId: resource.id.toString(),
      type: resource.type,
      folders: resource.folders?.map((folder) => folder.id.toString()) || [],
      name: resource.name,
      description: resource.description,
      thumbnail: resource.thumbnail,
      image: resource.image,
      document: resource.document,
      url: resource.url,
      video: resource.video,
      fileSize: resource.file_size,
      createdAt: resource.created_at,
      updatedAt: resource.updated_at,
    }

    const db = this.localContentService.getDatabase()

    return await db.resources.upsert(documentData)
  }

  async createFolder(name: string, parentFolderId: number | null): Promise<IFolderApiResponse> {
    const response: AxiosResponse<IFolderApiResponse> = await this.remoteContentService.request(
      'POST',
      '/api/folder/create',
      { name, parent_id: parentFolderId }
    )

    return response.data
  }

  async deleteFolders(folders: FolderDocument[]): Promise<void> {
    const response: AxiosResponse = await this.remoteContentService.request('DELETE', '/api/folder', {
      folders: folders.map((_) => _.id),
    })

    console.log('DELETE FOLDER RESPONSE', response)
  }

  async deleteResources(resources: ResourceDocument[]): Promise<void> {
    const response: AxiosResponse = await this.remoteContentService.request('DELETE', '/api/media', {
      media: resources.map((_) => _.id),
    })

    console.log('DELETE RESOURCES RESPONSE', response)
  }
}
